
/**
 * Utility functions for development cache management
 * These functions help with clearing cache during development
 */
import { isProduction } from './environment';

/**
 * Clear local browser cache for development
 * This can be used when UI changes aren't visible due to caching
 */
export const clearLocalCache = () => {
  if (!isProduction()) {
    try {
      // Clear localStorage items that might be causing caching issues
      const devCacheKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('app-cache-') || 
        key.includes('state') || 
        key.includes('version')
      );
      
      console.log(`Clearing ${devCacheKeys.length} cached items from localStorage`);
      
      devCacheKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Clear sessionStorage items
      const devSessionKeys = Object.keys(sessionStorage).filter(key => 
        key.startsWith('app-') || 
        key.includes('state') || 
        key.includes('version')
      );
      
      console.log(`Clearing ${devSessionKeys.length} cached items from sessionStorage`);
      
      devSessionKeys.forEach(key => {
        sessionStorage.removeItem(key);
      });
      
      // Additionally attempt to clear service worker caches if available
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            console.log(`Clearing cache: ${cacheName}`);
            caches.delete(cacheName);
          });
        });
      }
      
      console.log('Local cache cleared. Consider refreshing the page.');
      return true;
    } catch (error) {
      console.error('Failed to clear local cache:', error);
      return false;
    }
  }
  return false;
};

/**
 * Hard refresh the current page bypassing the cache
 */
export const hardRefresh = () => {
  if (!isProduction()) {
    try {
      clearLocalCache();
      // Force reload bypassing the cache
      window.location.reload();  // Removed the `true` parameter as it's not needed in modern browsers
      return true;
    } catch (error) {
      console.error('Failed to hard refresh:', error);
      return false;
    }
  }
  return false;
};

/**
 * Check if the page needs a hard refresh due to stale cache
 * This is especially useful for development environments
 */
export const checkForStaleCache = () => {
  if (!isProduction()) {
    const lastBuildTime = sessionStorage.getItem('last-build-time');
    const currentTime = Date.now().toString();
    
    if (!lastBuildTime) {
      // First load, set the build time
      sessionStorage.setItem('last-build-time', currentTime);
      return false;
    }
    
    // Check if we've exceeded the cache lifetime (5 minutes for dev)
    const timeDiff = Date.now() - parseInt(lastBuildTime, 10);
    if (timeDiff > 5 * 60 * 1000) { // 5 minutes
      console.log('Cache appears stale. Consider a hard refresh.');
      return true;
    }
  }
  return false;
};

/**
 * Add a button to the UI for hard refreshing in development mode
 */
export const addDevRefreshButton = () => {
  // Log environment information for debugging purposes
  console.log(`[DevRefresh] Environment check - NODE_ENV: ${process.env.NODE_ENV}`);
  console.log(`[DevRefresh] Environment check - isProduction(): ${isProduction()}`);
  console.log(`[DevRefresh] Environment check - URL: ${window.location.href}`);
  console.log(`[DevRefresh] Environment check - Hostname: ${window.location.hostname}`);

  // Only add in non-production environments
  if (!isProduction()) {
    try {
      // Remove any existing button first
      const existingButton = document.getElementById('dev-refresh-button');
      if (existingButton) {
        existingButton.remove();
      }
      
      // Create the button element
      const button = document.createElement('button');
      button.id = 'dev-refresh-button';
      button.textContent = '🔄 Dev Refresh';
      button.title = 'Clear cache and refresh';
      
      // Style the button
      Object.assign(button.style, {
        position: 'fixed',
        right: '10px',
        bottom: '10px',
        zIndex: '9999',
        padding: '8px 12px',
        backgroundColor: '#4a5568',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontWeight: '600',
        fontSize: '14px',
        cursor: 'pointer',
        opacity: '0.7',
        transition: 'opacity 0.2s'
      });
      
      button.addEventListener('mouseenter', () => {
        button.style.opacity = '1';
      });
      
      button.addEventListener('mouseleave', () => {
        button.style.opacity = '0.7';
      });
      
      // Add click handler
      button.addEventListener('click', () => {
        hardRefresh();
      });
      
      // Add to document
      document.body.appendChild(button);
      
      console.log('[DevRefresh] Dev refresh button added to page');
      return true;
    } catch (error) {
      console.error('[DevRefresh] Error adding dev refresh button:', error);
      return false;
    }
  } else {
    console.log('[DevRefresh] Not adding dev refresh button - production environment detected');
  }
  return false;
};
